import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { useParams, useSearchParams } from 'react-router-dom';

import B2BProductList from '@/components/B2BComponents/B2BProductProductList';
import B2BLoadingSkeletonProductList from '@/components/B2BComponents/LoadingSkeletonB2BProductList';
import { HaggleStoredFilters } from '@/components/filters/HaggleStoredFilters';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { HaggleInvite } from '@/interfaces/api/haggle-invite/haggleInvite';
import { INotifications } from '@/interfaces/api/notifications/INotifications';
import { ICollectionProduct } from '@/interfaces/api/product/ICollectionProduct';
import { IProduct } from '@/interfaces/api/product/IProduct';
import { ProductCardType } from '@/interfaces/notifications/Notifications';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const PrivateB2BMarketplace = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchParams] = useSearchParams();
	// const navigate = useNavigate();
	const [isVerified, setIsVerified] = useState(false);
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);

	const { merchantId, haggleEventId } = useParams<{
		merchantId: string;
		haggleEventId: string;
	}>();

	// Keeping this for future reference if we need to check the user role for link security
	// useEffect(() => {
	// 	if (!invitedUserEmail) return;

	// 	const checkUserExists = async () => {
	// 		try {
	// 			const user = await haggleApiService.getUserByEmail(invitedUserEmail);

	// 			if (
	// 				!user.roles.includes(UserRole.MerchantDiamond) &&
	// 				!user.roles.includes(UserRole.MerchantRuby)
	// 			) {
	// 				navigate(AppRoutes.UserDashboard);
	// 				notificationService.error(
	// 					'You are not allowed to access this page. Please upgrade your account to a diamond or ruby plan.',
	// 				);
	// 				return;
	// 			}
	// 		} catch (error: any) {
	// 			navigate(AuthRoutes.SignUp);
	// 		}
	// 	};

	// 	checkUserExists();
	// }, [invitedUserEmail, navigate]);

	const verifyPassword = async () => {
		try {
			const isValid = await haggleApiService.verifyHagglePassword(
				Number(haggleEventId),
				password,
			);
			if (isValid) {
				setIsVerified(true);
				notificationService.success('Password accepted');
			} else {
				notificationService.error('Invalid password. Please try again.');
			}
		} catch {
			notificationService.error(
				'Something went wrong while verifying password.',
			);
		}
	};

	const {
		data: products,
		isLoading: productsIsLoading,
		isError: productIsError,
		error: productError,
	} = useQuery<ICollectionProduct>({
		queryKey: [
			ApiQueryKeys.getAllHaggleProducts,
			pageSize,
			pageNumber,
			searchParams.toString(),
		],
		queryFn: () =>
			haggleApiService.getAllHaggleProducts({
				params: {
					'page[size]': pageSize,
					'page[number]': pageNumber,
					'filterQuery[vendor]': searchParams.getAll('vendor'),
					'filterQuery[productType]': searchParams.getAll('type'),
					'filterQuery[countryName]': searchParams.getAll('country'),
					'filterQuery[minPrice]': searchParams.get('minPrice')
						? Number(searchParams.get('minPrice'))
						: undefined,
					'filterQuery[maxPrice]': searchParams.get('maxPrice')
						? Number(searchParams.get('maxPrice'))
						: undefined,
					'filterQuery[status]': searchParams.get('status') || undefined,
					'filterQuery[showPublicEventsOnly]': undefined,
				},
			}),
		refetchOnWindowFocus: false,
	});

	const { user } = useUser();

	const filteredProducts: ICollectionProduct = {
		...products,
		data:
			products?.data?.filter(
				(product): product is IProduct =>
					'haggle' in product &&
					product.haggle?.id === Number(haggleEventId) &&
					product.haggle?.user?.id === Number(merchantId),
			) ?? [],
		pageNumber: products?.pageNumber ?? 1,
		pageSize: products?.pageSize ?? 10,
		pageCount: products?.pageCount ?? 1,
		itemCount: products?.itemCount ?? 0,
	};

	const {
		data: favouriteNotifications,
		isSuccess: isFavouritesLoaded,
		refetch: refetchFavourites,
	} = useQuery<INotifications[]>({
		queryKey: [ApiQueryKeys.getFavouritedNotifications],
		queryFn: () => haggleApiService.getFavouritedNotifications(),
		enabled: !!user,
		refetchOnWindowFocus: false,
	});

	const {
		data: userInvites,
		isSuccess: isUserInvitesLoaded,
		refetch: refetchUserInvites,
	} = useQuery<HaggleInvite[]>({
		queryKey: [ApiQueryKeys.getInvitesByEmail, user?.username],
		queryFn: () => haggleApiService.getHaggleInvitesByEmail(user!.username),
		enabled: !!user?.username,
		refetchOnWindowFocus: false,
	});

	if (productIsError) {
		if (productError instanceof Error) {
			notificationService.error(productError.message);
		} else {
			notificationService.error('Unknown error fetching products');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionProduct,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	if (!isVerified) {
		return (
			<div className="relative w-full h-full bg-gray-100 rounded-2xl overflow-hidden shadow-md">
				<div className="w-full h-[90vh] blur-sm brightness-40">
					<B2BLoadingSkeletonProductList />
				</div>

				<div className="absolute inset-0 z-10 flex items-center justify-center">
					<div className="bg-white/80 backdrop-blur-md p-6 rounded-xl shadow-lg w-full max-w-sm text-center">
						<h2 className="text-xl font-semibold text-gray-800 mb-4">
							Private Event
						</h2>
						<p className="text-sm text-gray-600 mb-4">
							Please enter password to continue
						</p>
						<div className="relative mb-4">
							<input
								type={showPassword ? 'text' : 'password'}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-white text-sm"
								placeholder="Enter password"
							/>
							<button
								type="button"
								onClick={() => setShowPassword(!showPassword)}
								className="absolute inset-y-0 right-3 flex items-center text-gray-500"
							>
								{showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
							</button>
						</div>
						<button
							onClick={verifyPassword}
							className="w-full bg-purpleBrand-500 text-white py-2 rounded-lg hover:bg-purpleBrand-600 transition font-medium text-sm"
						>
							Submit
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col">
			<div className="mb-2">
				<HaggleStoredFilters />
			</div>
			<div className="flex-1">
				{productsIsLoading && <B2BLoadingSkeletonProductList />}
				{products && !productsIsLoading && (
					<B2BProductList
						type={ProductCardType.Home}
						collectionProduct={filteredProducts}
						paginationOption={handlePaginationOptions(products)}
						favouriteMerchants={
							isFavouritesLoaded ? favouriteNotifications : []
						}
						isPrivateB2BMarketplace={true}
						refetchFavourites={refetchFavourites}
						invitedUsers={isUserInvitesLoaded ? userInvites : []}
						refetchUserInvites={refetchUserInvites}
					/>
				)}
			</div>
		</div>
	);
};

export default PrivateB2BMarketplace;
